import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Chip, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { loadBase, loadPlayerStats, loadAdvancedStats } from '../../services/espnService';
import { useConfig, CONFIG_DEFAULTS } from '../../hooks/useConfig';

// ── Reuse the same formula / stat-parsing logic as OffensiveNR ───────────────
const OFF_FORMULAS = {
  QB: ['=completionPct', 'passingYards', 'passingTouchdowns', '=~interceptionPct', '~sacks', 'rushingYards', 'rushingTouchdowns', 'passing_epa', '=cpoe', '=~pressure_pct'],
  RB: ['rushingYards', '=yardsPerRushAttempt', 'rushingTouchdowns', 'receivingYards', '=yardsPerReception', 'receivingTouchdowns', '~fumbles', 'rushing_epa', '=ybc_att', '=rush_yards_over_expected_per_att'],
  WR: ['receptions', 'receivingYards', 'receivingTouchdowns', 'receivingFirstDowns', 'rushingYards', 'rushingTouchdowns', '~fumbles', 'receiving_epa', '=avg_separation', '=~drop_percent'],
  TE: ['receptions', 'receivingYards', 'receivingTouchdowns', 'receivingFirstDowns', 'rushingYards', 'rushingTouchdowns', '~fumbles', 'receiving_epa', '=avg_separation', '=~drop_percent'],
};

const MIN_GAMES = { QB: 8, RB: 6, WR: 6, TE: 6 };
const POSITIONS = ['QB', 'RB', 'WR', 'TE'];
const POS_SLOTS = { QB: 1, RB: 2, WR: 3, TE: 2 };
const OFF_POS_CAPS = { QB: 33, RB: 65, WR: 97, TE: 65 };

const TEAM_NAMES = {
  ARI:'Cardinals', ATL:'Falcons', BAL:'Ravens', BUF:'Bills',
  CAR:'Panthers', CHI:'Bears', CIN:'Bengals', CLE:'Browns',
  DAL:'Cowboys', DEN:'Broncos', DET:'Lions', GB:'Packers',
  HOU:'Texans', IND:'Colts', JAX:'Jaguars', KC:'Chiefs',
  LAC:'Chargers', LAR:'Rams', LV:'Raiders', MIA:'Dolphins',
  MIN:'Vikings', NE:'Patriots', NO:'Saints', NYG:'Giants',
  NYJ:'Jets', PHI:'Eagles', PIT:'Steelers', SEA:'Seahawks',
  SF:'49ers', TB:'Buccaneers', TEN:'Titans', WSH:'Commanders',
};

function parseKey(key) {
  let k = key;
  const isRaw     = k.startsWith('='); if (isRaw)     k = k.slice(1);
  const isPenalty = k.startsWith('~'); if (isPenalty) k = k.slice(1);
  return { rawKey: k, isRaw, isPenalty };
}

function computePosNR(pos, players, stats, weights = {}) {
  const formula = OFF_FORMULAS[pos];
  const minGames = MIN_GAMES[pos] ?? 6;

  const eligible = players.filter(p => {
    const s = stats[p.id];
    return s && s.gamesPlayed >= minGames;
  }).map(p => {
    const s = stats[p.id];
    const gp = s.gamesPlayed;
    const perGame = {};
    formula.forEach(key => {
      const { rawKey, isRaw } = parseKey(key);
      const val = s[rawKey] ?? 0;
      perGame[key] = isRaw ? val : (gp > 0 ? val / gp : 0);
    });
    return { ...p, perGame, gamesPlayed: gp };
  });

  const maxRank = Math.min(eligible.length + 1, OFF_POS_CAPS[pos] ?? 65);

  if (!eligible.length) {
    const nrMap = {};
    players.forEach(p => { nrMap[p.id] = maxRank; });
    return { nrMap, maxRank };
  }

  const statRanks = {};
  eligible.forEach(p => { statRanks[p.id] = {}; });

  formula.forEach(key => {
    const { isPenalty } = parseKey(key);
    const sorted = [...eligible].sort((a, b) =>
      isPenalty ? a.perGame[key] - b.perGame[key] : b.perGame[key] - a.perGame[key]
    );
    let rank = 1, i = 0;
    while (i < sorted.length) {
      const tieVal = sorted[i].perGame[key];
      let j = i;
      while (j < sorted.length && sorted[j].perGame[key] === tieVal) {
        statRanks[sorted[j].id][key] = rank;
        j++;
      }
      rank += j - i;
      i = j;
    }
  });

  const withNR = eligible.map(p => {
    const rankSum = formula.reduce((acc, k) => {
      const w = weights[k] ?? 1;
      return acc + (statRanks[p.id][k] ?? eligible.length) * w;
    }, 0);
    return { ...p, rankSum };
  }).sort((a, b) => a.rankSum - b.rankSum);

  const nrMap = {};
  let nr = 1, i = 0;
  while (i < withNR.length) {
    const tieSum = withNR[i].rankSum;
    let j = i;
    while (j < withNR.length && withNR[j].rankSum === tieSum) {
      nrMap[withNR[j].id] = nr;
      j++;
    }
    nr += j - i;
    i = j;
  }

  players.forEach(p => {
    if (!(p.id in nrMap)) nrMap[p.id] = maxRank;
  });

  return { nrMap, maxRank };
}

// Fetch team-level pts scored per game from ESPN teams API
async function fetchTeamScoringStats() {
  try {
    const res  = await fetch('https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams?limit=32');
    const data = await res.json();
    const teamList = data?.sports?.[0]?.leagues?.[0]?.teams || [];
    const ids = teamList.map(t => t.team.id);
    const teams = await Promise.all(ids.map(id =>
      fetch(`https://site.api.espn.com/apis/site/v2/sports/football/nfl/teams/${id}`)
        .then(r => r.json())
        .then(d => d?.team || null)
        .catch(() => null)
    ));
    const map = {};
    teams.filter(Boolean).forEach(t => {
      const rec   = (t.record?.items || []).find(i => i.type === 'total') || {};
      const stats = rec.stats || [];
      const getStat = (arr, name) => arr.find(s => s.name === name)?.value ?? null;
      const wins   = getStat(stats, 'wins') ?? 0;
      const losses = getStat(stats, 'losses') ?? 0;
      const ties   = getStat(stats, 'ties') ?? 0;
      const games  = wins + losses + ties || null;
      const pf     = getStat(stats, 'pointsFor') ?? null;
      map[t.abbreviation] = { pf, games, ptsPg: (pf !== null && games) ? pf / games : null };
    });
    return map;
  } catch {
    return {};
  }
}

// ── Styles ────────────────────────────────────────────────────────────────────
const cellSx = {
  color: '#9eb8d0', fontSize: '0.75rem', borderColor: 'rgba(30,52,72,0.5)', py: 0.5, px: 0.75,
};
const headerCellSx = {
  color: '#2ea84b', fontWeight: 700, fontSize: '0.7rem', borderColor: 'rgba(30,52,72,0.5)',
  py: 0.75, px: 1, backgroundColor: '#060e1a',
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function OffensiveTeamNR() {
  const { config } = useConfig();
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [players, setPlayers]         = useState({});
  const [stats, setStats]             = useState({});
  const [teamScoring, setTeamScoring] = useState({});

  useEffect(() => {
    setLoading(true);
    Promise.all([
      loadBase().then(({ players: p }) => {
        const offPlayers = {};
        Object.values(p).forEach(pl => {
          if (POSITIONS.includes(pl.posKey)) offPlayers[pl.id] = pl;
        });
        return loadPlayerStats(Object.keys(offPlayers)).then(s => ({ offPlayers, s }));
      }),
      fetchTeamScoringStats(),
      loadAdvancedStats(),
    ])
      .then(([{ offPlayers, s }, scoring, advS]) => {
        // Merge advanced stats into base stats by ESPN player ID (additive)
        const merged = { ...s };
        Object.entries(advS).forEach(([espnId, adv]) => {
          if (merged[espnId]) merged[espnId] = { ...merged[espnId], ...adv };
        });
        setPlayers(offPlayers);
        setStats(merged);
        setTeamScoring(scoring);
        setLoading(false);
      })
      .catch(() => { setError('Failed to load offensive player data.'); setLoading(false); });
  }, []);

  const ptsWeight = config?.nr_weights_team_off?.ptsWeight
    ?? CONFIG_DEFAULTS.nr_weights_team_off?.ptsWeight
    ?? 1.5;

  const teamRankings = useMemo(() => {
    if (!Object.keys(players).length || !Object.keys(stats).length) return [];

    const posData = {};
    POSITIONS.forEach(pos => {
      const posPlayers = Object.values(players).filter(p => p.posKey === pos);
      const weights    = config[`nr_weights_off_${pos.toLowerCase()}`] ?? {};
      const { nrMap, maxRank } = computePosNR(pos, posPlayers, stats, weights);
      posData[pos] = { nrMap, maxRank, players: posPlayers };
    });

    const teamMap = {};
    Object.values(players).forEach(p => {
      if (!teamMap[p.team]) teamMap[p.team] = { QB: [], RB: [], WR: [], TE: [] };
      if (POSITIONS.includes(p.posKey)) teamMap[p.team][p.posKey].push(p);
    });

    const rows = Object.entries(teamMap).map(([team, byPos]) => {
      const posAvg     = {};
      const posPlayers = {};

      POSITIONS.forEach(pos => {
        const { nrMap, maxRank } = posData[pos];
        const slots = POS_SLOTS[pos] ?? 1;
        const teamPosPlayers = byPos[pos] || [];
        const playerNRs = teamPosPlayers.map(p => ({ name: p.shortName || p.name, nr: nrMap[p.id] ?? maxRank }));
        playerNRs.sort((a, b) => a.nr - b.nr);
        const topN   = playerNRs.slice(0, slots);
        const filled = [...topN];
        while (filled.length < slots) filled.push({ name: '—', nr: maxRank });
        posAvg[pos]     = filled.reduce((s, p) => s + p.nr, 0) / slots;
        posPlayers[pos] = playerNRs;
      });

      const posScore = POSITIONS.reduce((s, pos) => s + posAvg[pos], 0) / POSITIONS.length;
      const scoring  = teamScoring[team] ?? {};

      return { team, posScore, posContrib: posAvg, posPlayers, ptsPg: scoring.ptsPg ?? null };
    });

    // Blend pts-scored rank into total score
    if (Object.keys(teamScoring).length) {
      const withPts = rows.filter(r => r.ptsPg !== null);
      const sorted  = [...withPts].sort((a, b) => b.ptsPg - a.ptsPg); // higher pts = better
      const rankMap = {};
      sorted.forEach((r, i) => { rankMap[r.team] = i + 1; });
      const maxPtsRank = sorted.length + 1;
      const posCount   = POSITIONS.length;
      rows.forEach(r => {
        const ptsRank   = rankMap[r.team] ?? maxPtsRank;
        r.ptsRank       = ptsRank;
        r.totalScore    = (r.posScore * posCount + ptsRank * ptsWeight) / (posCount + ptsWeight);
      });
    } else {
      rows.forEach(r => { r.ptsRank = null; r.totalScore = r.posScore; });
    }

    rows.sort((a, b) => a.totalScore - b.totalScore);
    return rows.map((r, i) => ({ ...r, teamNR: i + 1 }));
  }, [players, stats, config, teamScoring, ptsWeight]); // eslint-disable-line

  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={36} sx={{ color: '#2ea84b' }} />
      <Typography variant="body2" sx={{ color: '#9eb8d0' }}>Computing team offensive ratings…</Typography>
    </Box>
  );

  if (error) return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ color: '#ef4444' }}>{error}</Typography>
    </Box>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#e8f0f8' }}>
          Team Offensive Nerd Rating
        </Typography>
        <Chip label="Nerds+" color="secondary" size="small" sx={{ fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
      </Box>
      <Typography variant="caption" sx={{ color: '#6a8aa8', display: 'block', mb: 2.5, lineHeight: 1.6 }}>
        Average NR of top starters per position (QB1 · RB1+2 · WR1+2+3 · TE1+2), blended with points-scored rank.
        Pts/G rank weight is adjustable in Admin. Lower total score = stronger offense.
      </Typography>

      <TableContainer component={Paper} sx={{ backgroundColor: '#060e1a', border: '1px solid rgba(30,52,72,0.6)', borderRadius: 1.5 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 48 }}>NR</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 130 }}>Team</TableCell>
              <TableCell align="center" sx={{ ...headerCellSx, width: 72 }}>Score ↓</TableCell>
              <TableCell align="center" sx={{ ...headerCellSx, width: 80 }}>Pts/G</TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top QB <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top RB <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top WR <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
              <TableCell sx={{ ...headerCellSx, minWidth: 110 }}>Top TE <span style={{ color: '#4a6080', fontWeight: 400 }}>(avg)</span></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teamRankings.map((row, idx) => (
              <TableRow key={row.team} sx={{
                backgroundColor: idx % 2 === 0 ? 'rgba(6,14,26,0.5)' : 'rgba(10,20,38,0.4)',
                '&:hover': { backgroundColor: 'rgba(22,44,70,0.6)' },
              }}>
                {/* Rank */}
                <TableCell sx={{
                  ...cellSx, fontWeight: 900, width: 48, fontSize: '0.82rem',
                  color: row.teamNR <= 5 ? '#2ea84b' : row.teamNR <= 10 ? '#f5a623' : '#e8f0f8',
                }}>
                  #{row.teamNR}
                </TableCell>

                {/* Team name */}
                <TableCell sx={{ ...cellSx, width: 130 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#e8f0f8', lineHeight: 1.2 }}>
                    {TEAM_NAMES[row.team] ?? row.team}
                  </Typography>
                  <Typography sx={{ fontSize: '0.62rem', color: '#4a6080', lineHeight: 1.2 }}>
                    {row.team}
                  </Typography>
                </TableCell>

                {/* Total score */}
                <TableCell align="center" sx={{ ...cellSx, fontWeight: 700, color: '#e8f0f8', width: 72 }}>
                  {row.totalScore.toFixed(1)}
                </TableCell>

                {/* Pts/G */}
                <TableCell align="center" sx={{ ...cellSx, width: 80 }}>
                  {row.ptsPg != null ? (
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#c0d4e8', fontFamily: 'monospace' }}>
                        {row.ptsPg.toFixed(1)}
                      </Typography>
                      {row.ptsRank != null && (
                        <Typography sx={{ fontSize: '0.58rem', color: '#4a6080' }}>#{row.ptsRank}</Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography sx={{ fontSize: '0.7rem', color: '#3a5570' }}>—</Typography>
                  )}
                </TableCell>

                {/* Top player + position avg per position */}
                {POSITIONS.map(pos => {
                  const top = row.posPlayers[pos]?.[0];
                  const avg = row.posContrib[pos];
                  return (
                    <TableCell key={pos} sx={{ ...cellSx }}>
                      {top ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography sx={{ fontSize: '0.73rem', color: '#c0d4e8' }}>{top.name}</Typography>
                          <Chip
                            label={`#${top.nr}`}
                            size="small"
                            sx={{
                              fontSize: '0.58rem', height: 16, fontWeight: 700,
                              bgcolor: top.nr <= 5 ? 'rgba(245,166,35,0.15)' : 'rgba(255,255,255,0.05)',
                              color: top.nr <= 5 ? '#f5a623' : '#6a8aa8',
                              border: `1px solid ${top.nr <= 5 ? 'rgba(245,166,35,0.3)' : 'rgba(255,255,255,0.08)'}`,
                            }}
                          />
                          {avg != null && (
                            <Typography sx={{ fontSize: '0.62rem', color: '#4a6080' }}>
                              avg {avg.toFixed(1)}
                            </Typography>
                          )}
                        </Box>
                      ) : <Typography sx={{ fontSize: '0.7rem', color: '#3a5570' }}>—</Typography>}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
